import { createFeatureSelector, createSelector } from '@ngrx/store'

import * as fromRoot from '../../reducers'
import * as fromUser from './user'
import * as fromAuth from './auth.reducer'
import * as fromLoginPage from './login-page.reducer'
import * as fromCustomer from './customer'
import * as fromCreateAccount from './create-account'
import * as fromResetPassword from './reset-password'
import * as fromActivate from './activate-account'
import * as fromAccount from './account.reducer'
import { parseEntities } from '../../core/helpers/entities';

export interface AuthState {
  status: fromAuth.State
  user: fromUser.State,
  customer: fromCustomer.State,
  loginPage: fromLoginPage.State
  createAccount: fromCreateAccount.State
  resetPassword: fromResetPassword.State
  activateAccount: fromActivate.State
  account: fromAccount.State
}

export interface State extends fromRoot.State {
  auth: AuthState;
}

export const reducers = {
  status: fromAuth.reducer,
  user: fromUser.reducer,
  customer: fromCustomer.reducer,
  loginPage: fromLoginPage.reducer,
  createAccount: fromCreateAccount.reducer,
  resetPassword: fromResetPassword.reducer,
  activateAccount: fromActivate.reducer,
  account: fromAccount.reducer,
}

export const selectAuthState = createFeatureSelector<AuthState>('auth');

// Auth
export const selectAuthStatusState = createSelector(
  selectAuthState,
  (state: AuthState) => state.status
)

export const getLoggedIn = createSelector(
  selectAuthStatusState,
  fromAuth.getLoggedIn
)
export const getToken = createSelector(
  selectAuthStatusState,
  fromAuth.getToken
)
export const getRefreshtoken = createSelector(
  selectAuthStatusState,
  fromAuth.getRefreshToken
)

// User
export const selectUserState = createSelector(
  selectAuthState,
  (state: AuthState) => state.user
)
export const getUserContext = createSelector(
  selectUserState,
  fromUser.getUserContext
)
export const getUserId = createSelector(
  selectUserState,
  fromUser.getUserId
)
export const getUserError = createSelector(
  selectUserState,
  fromUser.getError
)
export const isLoadingContext = createSelector(
  selectUserState,
  fromUser.isLoading
)
export const getUser = createSelector(
  selectUserState,
  fromUser.getUserContext
)
export const getAddresses = createSelector(
  selectUserState,
  fromUser.getAddresses
)
export const getCurrency = createSelector(
  selectUserState,
  fromUser.getCustomerCurrency
)
export const getLanguage = createSelector(
  selectUserState,
  fromUser.getLanguage
)
export const isSalesRep = createSelector(
  getUserContext,
  context => context && context.hasMoreCustomers
)
export const getUserPermissions = createSelector(
  selectUserState,
  fromUser.getUserPermissions
)
export const isAdmin = createSelector(
  getUserContext,
  context => {
    let isAdmin: boolean = false
    if (context && context.roles && context.roles.length > 0) {
      for (var i = 0; i < context.roles.length; i++) {
        if (context.roles[i] === 'admin') {
          isAdmin = true
          break
        }
      }
    }
    return isAdmin
  }
)
export const isCustomerSelected = createSelector(
  selectUserState,
  getUserContext,
  (state, context) => {
    return (!!state.customerSelected || !!(context && context.customer && Object.keys(context.customer).length))
  }
)
export const getCustomerId = createSelector(
  selectUserState,
  getUserContext,
  fromUser.getCustomerInfo,
  fromUser.getCustomerId
)
export const getSelectedCustomerId = createSelector(
  selectUserState,
  state => state && state.customerSelected
)
export const getCurrentTenantName = createSelector(
  selectUserState,
  fromUser.getCurrentTenant
)
export const getCustomerMinOrderValue = createSelector(
  selectUserState,
  fromUser.getCustomerMinOrderValue,
)
export const getCustomerDiscounts = createSelector(
  selectUserState,
  fromUser.getCustomerDiscounts,
)
export const getSelectedCustomer = createSelector(
  selectUserState,
  fromUser.getCustomerInfo,
)

// Customers
export const selectCustomerState = createSelector(
  selectAuthState,
  (state: AuthState) => state.customer
)

export const getCustomersEntities = createSelector(
  selectCustomerState,
  state => state && state.customers
)

export const customersIsLoading = createSelector(
  selectCustomerState,
  state => state.loading
)

export const getCustomersList = createSelector(
  getCustomersEntities,
  entities => {
    return parseEntities(entities)
  }
)

export const customersCanExpand = createSelector(
  selectCustomerState,
  state => state.totalPages > state.currentPage
)

export const getNextCustomerPage = createSelector(
  selectCustomerState,
  state => state.nextPage
)

export const getCustomerSearchTerm = createSelector(
  selectCustomerState,
  state => state.searchTerm
)

// Login Page
export const selectLoginPageState = createSelector(
  selectAuthState,
  (state: AuthState) => state.loginPage
)
export const getLoginPageError = createSelector(
  selectLoginPageState,
  fromLoginPage.getError
)
export const getLoginPagePending = createSelector(
  selectLoginPageState,
  fromLoginPage.getPending
)
export const getReturnUrl = createSelector(
  selectLoginPageState,
  fromLoginPage.getReturnUrl
)
export const isLoginWorking = createSelector(
  getLoginPagePending,
  isLoadingContext,
  (loggingIn, loading) => loggingIn || loading
)

// Create Account
export const selectCreateAccountState = createSelector(
  selectAuthState,
  (state: AuthState) => state.createAccount
)
export const isCreating = createSelector(
  selectCreateAccountState,
  fromCreateAccount.isCreating
)
export const getCreateError = createSelector(
  selectCreateAccountState,
  fromCreateAccount.getError
)

// Reset Password
export const selectResetPasswordState = createSelector(
  selectAuthState,
  (state: AuthState) => state.resetPassword
)
export const isResetting = createSelector(
  selectResetPasswordState,
  fromResetPassword.isResetting
)
export const getResetError = createSelector(
  selectResetPasswordState,
  fromResetPassword.getError
)
export const isResetTokenValidationCompleted = createSelector(
  selectResetPasswordState,
  fromResetPassword.isValidationCompleted,
)
export const isResetTokenValid = createSelector(
  selectResetPasswordState,
  fromResetPassword.isResetLinkValid,
)
export const getPageLanguage = createSelector(
  selectResetPasswordState,
  fromResetPassword.getLanguage,
)

// Activate Account
export const selectActivateState = createSelector(
  selectAuthState,
  (state: AuthState) => state.activateAccount
)
export const isWorking = createSelector(
  selectActivateState,
  fromActivate.isWorking
)
export const isSuccessful = createSelector(
  selectActivateState,
  fromActivate.isSuccessful
)
export const getActivateError = createSelector(
  selectActivateState,
  fromActivate.getError
)

// Account
export const selectAccountState = createSelector(
  selectAuthState,
  (state: AuthState) => state.account
)
export const isVerifyEmailWorking = createSelector(
  selectAccountState,
  fromAccount.isVerifyEmailWorking
)
export const isVerifyEmailSuccess = createSelector(
  selectAccountState,
  fromAccount.isVerifyEmailSuccess
)
export const getVerifyEmailError = createSelector(
  selectAccountState,
  fromAccount.getVerifyEmailError
)
export const getAccountValidationResult = createSelector(
  selectAccountState,
  fromAccount.getAccountValidationResult
)

// Complex
export const isLoggedIn = createSelector(
  isLoginWorking,
  getLoggedIn,
  (working, loggedIn) => !working && loggedIn
)
